import { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Send, Building } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UniversityInquiryDialog = ({ open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    university_name: '',
    email: '',
    phone: '',
    message: '',
  });

  const resetForm = () =>
    setForm({ name: '', university_name: '', email: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.university_name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/inquiries/university`, form);
      toast.success("Thanks for reaching out! Our partnerships team will contact you within 24 hours.");
      resetForm();
      onOpenChange(false);
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        'Unable to submit your inquiry. Please try again shortly.';
      toast.error(typeof detail === 'string' ? detail : 'Unable to submit your inquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-white dark:bg-slate-800"
        data-testid="university-inquiry-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
              <Building className="w-5 h-5 text-teal-700 dark:text-teal-400" />
            </div>
            <div>
              <DialogTitle className="text-gray-800 dark:text-white">
                University Partnership Inquiry
              </DialogTitle>
              <DialogDescription className="text-gray-500 dark:text-gray-400">
                Tell us about your university and our partnerships team will get in touch.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">
                Full Name<span className="text-rose-500">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="university-inquiry-name"
                placeholder="Jane Doe"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">
                University<span className="text-rose-500">*</span>
              </Label>
              <Input
                value={form.university_name}
                onChange={(e) => setForm({ ...form, university_name: e.target.value })}
                required
                data-testid="university-inquiry-university"
                placeholder="University of..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">
                Work Email<span className="text-rose-500">*</span>
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="university-inquiry-email"
                placeholder="you@university.edu"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="university-inquiry-phone"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-600 dark:text-gray-300">
              Message<span className="text-rose-500">*</span>
            </Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={4}
              data-testid="university-inquiry-message"
              placeholder="Tell us about programmes, student intake goals, or anything else relevant..."
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="university-inquiry-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="university-inquiry-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Inquiry
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UniversityInquiryDialog;
